/**
 * THE CROSSHAIR IS THE AIM — the single shared client rule that makes the
 * screen-centre reticle decide where the ball goes.
 *
 * Owner, 2026-07-25, verbatim: *"keep the cross air always on the screen and
 * lets use the cross to decide where the ball will go instead of the arrow near
 * his foot. and dont show the arrows near his foot"*.
 *
 * ── Why this file has to exist (the aim was NOT already the crosshair) ──────
 *
 * The obvious reading is that nothing was needed: the mouse already steers the
 * aim, the mouse already steers the camera, so the screen centre must already be
 * the strike direction. It was not, and the divergence is structural — the mouse
 * drives TWO independent accumulators in `input/InputManager.ts feedMouseMove`:
 *
 *   this.aimYaw        += dx * this.aimSensitivity     // ALWAYS  (0.0014)
 *   this.cameraLookYaw += dx * this.mouseSensitivity   // only when NOT charging (0.0018)
 *
 * and the camera's azimuth is fed from `cameraLookYaw` alone (every loop sets
 * `camInput.playerYaw = input.state.cameraLookYaw`; `render/camera.ts`'s orbit
 * yaw has, by §10 contract, "no other term"). Four separate ways they disagreed:
 *
 *   1. DIFFERENT GAIN — 0.0014 vs 0.0018. The same mouse travel turned the aim
 *      22% less far than the view, so they drifted apart continuously.
 *   2. THE CAMERA IS FROZEN WHILE CHARGING (feedMouseMove's `if (shootHeld ||
 *      passHeld)` branch writes curl, never cameraLookYaw) while `aimYaw` keeps
 *      steering — so during the ≤1.2 s that aiming actually matters the two were
 *      not merely offset, they were moving in different directions.
 *   3. THE AIM SNAPS TO THE RUN on the charge's first tick (`aimInitFromRun`,
 *      InputManager.ts:772) — so a charge began pointing along the LEGS, not
 *      along the view.
 *   4. The camera's own orbit yaw is smooth-damped and acceleration-capped
 *      toward `cameraLookYaw`, so it lags it after a flick.
 *
 * With the feet arrow drawn, that was survivable: the arrow was a live readout
 * of `aimYaw`, so the player aimed by the arrow and the crosshair was decoration.
 * Deleting the arrow (the owner's third instruction) deletes the only display
 * `aimYaw` ever had — an invisible aim. So the aim must become the thing that IS
 * displayed. That is this file.
 *
 * ── What "where the crosshair points" means, exactly ───────────────────────
 *
 * The reticle is pinned to the viewport centre by a CSS percentage (see
 * `ui/crosshair-geometry.ts`), so it marks the camera's own forward ray, and the
 * camera pose gives that ray exactly: from the eye at (x, z) through the look
 * point at (lookX, lookZ). Projected to the ground plane its heading is
 * `atan2(lookZ - z, lookX - x)`, which is what `crosshairAimYaw` returns.
 *
 * Deriving it from the POSE rather than re-deriving `cameraLookYaw` matters:
 * the pose is the smoothed/acceleration-capped orbit the player is actually
 * looking down, and it already folds in Ball Focus (which eases the look point
 * toward the ball, `camera.ts` updateTacticalFollow) and every per-mode framing
 * rule. Whatever the camera does, this is the direction under the ticks.
 *
 * Treating the reticle as a DIRECTION and not a point is deliberate: a
 * screen-centre reticle has no depth, so there is no parallax term to correct —
 * the eye, the follow point (≈ the carrier) and the reticle's ground point are
 * collinear in plan view, so the heading from the player toward it is the
 * camera's heading.
 *
 * ── Scope, and what this deliberately does NOT do ──────────────────────────
 *
 * This is a CLIENT seam, exactly like `collectionAssist.ts` and the
 * pass-to-highlight override next to it in every loop: it rewrites the local
 * player's `InputCommand.aimYaw` before the command is stepped/sent. `aimYaw` is
 * on the wire, so online the server's authoritative strike uses the same number
 * the client predicted — no sim change, no wire change, no new WorldState field.
 * `packages/shared/src/**` is untouched.
 *
 * It does NOT touch possession. Whether you may kick at all is still
 * `possessionArrow.ts`'s job, and the sim's own strike gates (BALL_PLAY_REACH,
 * §3.4) still decide whether a kick happens. This only answers "along which
 * heading", and only on the ticks where a strike aim exists.
 *
 * FIXED, 2026-07-25 (was: "the aim is frozen for the length of a charge"). The
 * camera used to freeze while a shot/pass charged (point 2 above), and once the
 * aim followed the camera that froze the AIM too — you could point, hold and
 * release, but not correct mid-hold, which you could before the rewire. The fix
 * was where this note said it would be, in `feedMouseMove`: the mouse keeps
 * writing `cameraLookYaw` through a charge, at the finer `aimSensitivity` gain.
 * NOT a second aim path here — there is still exactly one heading, and the
 * reticle still cannot move away from where the ball will go.
 */
import { InputFlags, type InputCommand } from '@fg/shared/sim';
import { CameraMode, type CameraPose } from '../render/camera.js';

/**
 * Can the reticle aim in this camera mode AT ALL?
 *
 * ⚠ THE ONE THING THAT NEARLY MADE THIS FEATURE A DISASTER. Not every camera in
 * §7 turns with the mouse. `render/camera.ts updateFieldCam` is a FIXED
 * touchline rig: `camZ = smoothZ + distance`, `lookX = camX`, and the aim ray is
 * built straight down −Z. There is no yaw term anywhere in it, by design (D0 —
 * the EA-FC broadcast look). So in Broadcast / Tele / Dynamic the screen-centre
 * ray points across the pitch and NEVER MOVES: had `applyCrosshairAim` been let
 * loose there, every shot and pass would have flown at a constant −90°, into the
 * far touchline, regardless of the mouse. `crosshairAim.test.ts` pins this
 * against the REAL CameraSystem (sweep the look yaw, see whether the pose
 * heading follows) rather than against my reading of it, so if a mode's coupling
 * ever changes, that test fails rather than the game.
 *
 * Mouse-steered, therefore aimable:
 *   - TacticalFollow — orbits the play on the mouse-look yaw and nothing else (§10)
 *   - PlayerCam / ProCam — over-the-shoulder, `smoothYaw` tracks the look yaw
 *
 * Fixed, therefore NOT aimable: Broadcast, Tele, Dynamic (all touchline rigs) and
 * Free (driven externally for spectator/replay).
 *
 * Where this is false the loops feed NaN as the crosshair heading, which makes
 * `applyCrosshairAim` a no-op (the pre-existing aimYaw path is left exactly as
 * it was) and hides the reticle — a screen-centre crosshair that cannot steer is
 * the very lie the owner's instruction exists to remove.
 *
 * ⚠ THE DEFAULT MODE MUST BE ONE OF THE TRUE ONES. That consequence was left
 * open when this file landed, and it made the out-of-box game strictly worse
 * than before the owner's instruction: Broadcast was the default, so the camera
 * he actually plays in had no aim indicator AND no crosshair aiming, with the
 * feet arrow already deleted. The default is now TacticalFollow (ui/settings.ts
 * DEFAULT_CAMERA_MODE — read it for why the alternative, bolting a mouse orbit
 * onto the broadcast rig, buys the same thing at the cost of D0's look).
 * `game/defaultCameraAim.test.ts` pins the pairing, so a future camera-default
 * change fails a test rather than silently un-aiming the game.
 */
export function cameraAimsWithCrosshair(mode: CameraMode): boolean {
  switch (mode) {
    case CameraMode.TacticalFollow:
    case CameraMode.PlayerCam:
    case CameraMode.ProCam:
      return true;
    case CameraMode.Broadcast:
    case CameraMode.Tele:
    case CameraMode.Dynamic:
    case CameraMode.Free:
      return false;
  }
}

/**
 * The ground-plane heading the screen-centre reticle points along, in the sim's
 * yaw convention (atan2(z, x); yaw 0 faces +X — same convention `cmd.aimYaw`
 * and `player-controller.ts` use).
 *
 * Returns NaN when the pose is degenerate (eye and look point coincident, which
 * only happens before the first camera update has run). Callers treat NaN as
 * "no crosshair heading yet" and leave the command alone, so the very first
 * frame of a mount can never write a garbage aim.
 */
export function crosshairAimYaw(pose: CameraPose): number {
  const dx = pose.lookX - pose.x;
  const dz = pose.lookZ - pose.z;
  // 1e-6 m² — a camera this close to its own look point has no meaningful
  // horizontal heading (a top-down pose would be the real case).
  if (dx * dx + dz * dz < 1e-6) return Number.NaN;
  return Math.atan2(dz, dx);
}

/**
 * Point a strike along the crosshair.
 *
 * Applied on two kinds of tick, and no others:
 *   - the tick a shoot/pass flag actually goes out (the kick itself — this is
 *     the one that decides where the ball goes), and
 *   - every tick of the charge that precedes it (`charging`), so the BODY turns
 *     toward the reticle while you hold, and so the pass-target preview the
 *     loop draws from the same heading agrees with the man the strike reaches.
 *
 * Everything else is left exactly as `buildInputCommand` built it — off-ball
 * facing still comes from the movement heading (§7 decouple: a jittery mouse
 * must never spin a running player), and `collectionAssist`'s "face the
 * approach" while chasing a loose ball is untouched, because a loose ball is by
 * definition not a strike.
 *
 * Returns whether it applied, so a loop can report/assert on it.
 */
export function applyCrosshairAim(cmd: InputCommand, crosshairYaw: number, charging: boolean): boolean {
  if (!Number.isFinite(crosshairYaw)) return false;
  const striking = (cmd.flags & (InputFlags.Shoot | InputFlags.Pass)) !== 0;
  if (!striking && !charging) return false;
  cmd.aimYaw = crosshairYaw;
  return true;
}

// ─────────────────────────────────────────────────────────────────────────────
// The reticle's two gates — the shared rule all four loops agree through, the
// same way possessionArrow.ts is the shared rule for ownership.
//
// Split in two because the owner's *"always on the screen"* is a DISPLAY
// instruction and must not quietly become *"you can always kick"*:
//
//   SHOWN  = the player is looking at live play, and hasn't switched the reticle
//            off in Settings > HUD. NOT possession.
//   ARMED  = shown, AND the controlled player really owns the ball, so a strike
//            would fire down this ray. Drives full-vs-dim opacity and whether
//            the ball-destination dot is drawn at all.
//
// Deliberately booleans rather than a params object: these run once per frame in
// the render half of every loop, and §15.7 keeps that path allocation-free.
// ─────────────────────────────────────────────────────────────────────────────

/**
 * Is the reticle drawn?
 *
 * `interrupted` is the caller's OR of every state in which the player is not
 * looking at live play — a pause menu, the full-time board, the kickoff intro, a
 * goal replay, the settings overlay. "Always on" means "does not blink out when
 * you lose the ball"; it does not mean "floats over a pause menu", so those
 * cases stay excluded and each loop lists the ones it actually has.
 */
export function crosshairShown(settingEnabled: boolean, interrupted: boolean): boolean {
  return settingEnabled && !interrupted;
}

/**
 * Is the reticle claiming a strike?
 *
 * `hasBall` is `possessionArrow.ts`'s `possessionArrowVisible(...)` — controlled
 * possession in live play. Armed IMPLIES shown, so there is no "armed but
 * invisible" state for a caller to get wrong, and an armed reticle can never
 * outlive the display gate that draws it.
 *
 * This is display state ONLY. Whether a kick is legal remains the sim's business
 * (§3.4 BALL_PLAY_REACH and the possession model); relaxing the reticle's
 * visibility deliberately relaxed nothing about striking.
 */
export function crosshairArmed(settingEnabled: boolean, interrupted: boolean, hasBall: boolean): boolean {
  return crosshairShown(settingEnabled, interrupted) && hasBall;
}
